import { memo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES, MIN_IMAGE_SIZE } from '../../entityTypes';
import type { CanvasEntity } from '../../entityTypes';
import { getImageData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { CanvasResizeHandles } from '../../components/CanvasResizeHandles';

export interface CanvasImageProps {
  entity: CanvasEntity;
}

function CanvasImageInner({ entity }: CanvasImageProps) {
  const { store } = useCanvasStores();
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();

  const d = getImageData(entity);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const handleResize = useCallback((x: number, y: number, width: number, height: number) => {
    store.send({ type: 'entity.resize', entityId: entity.id, x, y, width, height });
  }, [store, entity.id]);

  return (
    <div
      data-canvas-entity
      data-canvas-image={entity.id}
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: entity.width,
        height: entity.height,
        border: d.borderColor ? `${d.borderWidth ?? 1}px solid ${d.borderColor}` : undefined,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
    >
      <img
        src={d.src}
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'fill',
          borderRadius: d.borderRadius,
          opacity: d.opacity,
          pointerEvents: 'none',
          display: 'block',
        }}
      />

      {/* Resize handles when selected */}
      {isSelected && !isLocked && (
        <CanvasResizeHandles
          x={entity.x}
          y={entity.y}
          width={entity.width}
          height={entity.height}
          minWidth={MIN_IMAGE_SIZE}
          minHeight={MIN_IMAGE_SIZE}
          onResize={handleResize}
          lockAspectRatio
        />
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}
            entityWidth={entity.width}
            entityHeight={entity.height}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasImage = memo(CanvasImageInner);
